'use client'

import {
  GuideContent,
  GuildContentFooter,
} from '@/app/[locale]/product/[slug]/_components/product-info-sizes/guide-content'
import { SizeTableContent } from '@/app/[locale]/product/[slug]/_components/product-info-sizes/size-table-content'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs } from '@/components/ui/tabs'
import { ProductDetailFormatted } from '@/types/product-detail'
import { SizeDataCategory } from '@/types/size'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import ProductInfoSizeMale from '../product-info-size-male'

type TabOption = {
  label: string
  value: string
  content: React.ReactNode
}

type Props = {
  onCancel: () => void
  tabDefault: string
  sizeChart: string
  gender_type: string
  size_guide: SizeDataCategory
  productId?: string
  product: ProductDetailFormatted
}

export const InstructionsSizeDrawer: React.FC<Props> = ({
  onCancel,
  tabDefault,
  sizeChart,
  gender_type,
  size_guide,
  productId,
  product,
}) => {
  const t = useTranslations('product_detail.instruction_size')
  const isBraOrLegging = sizeChart === 'BRA' || sizeChart === 'LEGGING'
  const [activeTab, setActiveTab] = useState(
    isBraOrLegging ? tabDefault || 'size-table' : 'size-table'
  )

  const tabOptions = useMemo(() => {
    const options: TabOption[] = [
      {
        label: t('size_table'),
        value: 'size-table',
        content: <SizeTableContent sizeChart={sizeChart} />,
      },
    ]

    if (isBraOrLegging) {
      options.unshift({
        label: t('guide'),
        value: 'guide',
        content: <GuideContent sizeChart={sizeChart} />,
      })
    }

    return options
  }, [isBraOrLegging, sizeChart, t])

  const showFooter = activeTab === 'guide' && isBraOrLegging

  const renderContent = () => {
    if (isBraOrLegging) {
      return (
        <Tabs
          variant='neutral'
          options={tabOptions}
          onChange={setActiveTab}
          defaultValue={activeTab}
          className='h-fit border-b border-b-neutral-200 pb-0'
          triggerClassName='flex-1 border-b-[3px] font-semibold font-sans text-sm leading-4.5'
        />
      )
    }

    return size_guide ? (
      <ErrorBoundary
        fallback={
          <div className='mt-2 text-center text-sm text-error-500'>
            {t('error_displaying_size_table')}
          </div>
        }
      >
        {gender_type !== 'FEMALE' ? (
          <ProductInfoSizeMale
            productId={productId}
            product={product}
            sizeChartOverride={sizeChart || undefined}
            sizeGuideOverride={size_guide || undefined}
          />
        ) : (
          <SizeTableContent
            sizeChart={sizeChart}
            shapeType={product?.shape_type}
          />
        )}
      </ErrorBoundary>
    ) : null
  }

  if (renderContent() === null) {
    return null
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel()
    }
  }

  return (
    <Sheet open onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='scrollbar bottom-0 top-[unset] flex h-[80vh] w-full flex-col justify-between gap-12 overflow-y-auto px-0 pb-0 pt-10 lg:top-0 lg:h-screen lg:w-[640px] lg:pt-17'
        aria-describedby='mobile-filter'
        showCloseButton={false}
      >
        <div className='bg-light px-8 pb-5 lg:px-15'>
          <SheetTitle className='mb-6 font-criteria text-[28px] font-semibold leading-10'>
            {t('drawer_title')}
          </SheetTitle>
          <button
            className='absolute right-1 top-1 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-light bg-neutral-900 text-light shadow-[0px_3.63px_4.54px_0px_rgba(0,0,0,0.25)] hover:bg-neutral-800 disabled:pointer-events-none lg:-right-4 lg:-top-4 lg:hidden'
            onClick={onCancel}
          >
            <X className='h-4 w-4' strokeWidth={3} />
            <span className='sr-only'>Close</span>
          </button>

          <SheetDescription />

          {renderContent()}
        </div>

        {showFooter && <GuildContentFooter sizeChart={sizeChart} />}
      </SheetContent>
    </Sheet>
  )
}
